import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// Updated ApiSnapshot interface strictly matching API response
interface ApiSnapshot {
  id: string;
  title: string;
  description: string | null;
  snapshot_type: 'auto' | 'manual' | 'step' | 'milestone' | 'backup';
  version_number: number;
  is_auto_save: boolean;
  key_information: {
    submit: string;
    is_draft: string;
    timeline: string;
    other_software: string;
    uploaded_files: any[]; // changed from string → array
    current_process: string;
    evaluation_type: string;
    organization_id: string;
    estimated_impact: string;
    delivery_preferences: string;
    opportunity_statement: string;
    proposed_enhancements: string;
    enterprise_applications_in_use: string[];
  };
  data_sources: {
    current_data_sources: any[];
    additional_data_sources: any[];
  };
  elaborated_summary?: {
    solution_name: string;
    elaborated_documents: {
      id: string;
      title: string;
      content: string; // fixed key to lowercase "content"
      version: number;
      file_url: string | null;
      file_name: string | null;
      file_size: number | null;
      created_at: string;
      updated_at: string;
      description: string;
      new_solution_id: string;
    };
  };
  detailed_report?: Record<string, any>; // can be {} or object
  process_status: string;
  sub_process_status: string;
  step_name: string | null;
  custom_metadata: Record<string, any>;
  restored_by?: string | null; // newly added field
  solution_id: string;
  user_id: string;
  user_name: string;
  created_at: string;
  updated_at: string;
}

// Processed snapshot for UI display
interface ProcessedSnapshot {
  id: string;
  title: string;
  description: string;
  type: 'auto' | 'manual' | 'step' | 'milestone' | 'backup';
  version: number;
  isAutoSave: boolean;
  userId: string;
  userName: string;
  userInitials: string;
  createdAt: string;
  timestamp: number;
  timeAgo: string;
  displayTime: string;
  solutionId: string;
  data: {
    keyInformation: ApiSnapshot['key_information'];
    dataSources: {
      internal: any[];
      external: any[];
    };
    processStatus: string;
    step: string;
    metadata?: Record<string, any>;
  };
}

// Version history state
interface VersionHistoryState {
  snapshots: ProcessedSnapshot[];
  loading: boolean;
  error: string | null;
  currentSolutionId: string | null;
  totalCount: number;
  page: number;
  pageSize: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

const initialState: VersionHistoryState = {
  snapshots: [],
  loading: false,
  error: null,
  currentSolutionId: null,
  totalCount: 0,
  page: 1,
  pageSize: 20,
  hasNext: false,
  hasPrevious: false,
};

// Helpers
const formatTimeAgo = (timestamp: number): string => {
  const now = Date.now();
  const diffInMinutes = Math.floor((now - timestamp) / (1000 * 60));

  if (diffInMinutes < 1) return 'Just now';
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`;

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours}h ago`;

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 30) return `${diffInDays}d ago`;

  return new Date(timestamp).toLocaleDateString();
};

const generateUserInitials = (userName: string | null): string => {
  if (!userName) return 'UN';
  return userName
    .split(' ')
    .map((name) => name[0])
    .join('')
    .toUpperCase()
    .substring(0, 2);
};

const processSnapshot = (
  apiSnapshot: ApiSnapshot,
  defaultUserName: string = 'Unknown User'
): ProcessedSnapshot => {
  const timestamp = new Date(apiSnapshot.created_at).getTime();
  const userName = apiSnapshot.user_name || defaultUserName;

  return {
    id: apiSnapshot.id,
    title: apiSnapshot.title,
    description: apiSnapshot.description || '',
    type: apiSnapshot.snapshot_type,
    version: apiSnapshot.version_number,
    isAutoSave: apiSnapshot.is_auto_save,
    userId: apiSnapshot.user_id,
    userName,
    userInitials: generateUserInitials(userName),
    createdAt: apiSnapshot.created_at,
    timestamp,
    timeAgo: formatTimeAgo(timestamp),
    displayTime: new Date(timestamp).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    }),
    solutionId: apiSnapshot.solution_id,
    data: {
      keyInformation: apiSnapshot.key_information,
      dataSources: {
        internal: apiSnapshot.data_sources?.current_data_sources || [],
        external: apiSnapshot.data_sources?.additional_data_sources || [],
      },
      processStatus: apiSnapshot.process_status || 'draft',
      step: apiSnapshot.step_name || 'generalInformation',
      metadata: apiSnapshot.custom_metadata || {},
    },
  };
};

// Slice
const versionHistorySlice = createSlice({
  name: 'versionHistory',
  initialState,
  reducers: {
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
      if (action.payload) state.error = null;
    },
    setError: (state, action: PayloadAction<string>) => {
      state.error = action.payload;
      state.loading = false;
    },
    setCurrentSolutionId: (state, action: PayloadAction<string>) => {
      state.currentSolutionId = action.payload;
    },
    updateSnapshots: (
      state,
      action: PayloadAction<{
        snapshots: ApiSnapshot[];
        totalCount?: number;
        page?: number;
        pageSize?: number;
        hasNext?: boolean;
        hasPrevious?: boolean;
        defaultUserName?: string;
      }>
    ) => {
      const {
        snapshots,
        totalCount,
        page,
        pageSize,
        hasNext,
        hasPrevious,
        defaultUserName = 'Unknown User',
      } = action.payload;

      const processed = snapshots
        .map((s) => processSnapshot(s, defaultUserName))
        .sort((a, b) => b.timestamp - a.timestamp);

      state.snapshots = processed;
      state.totalCount = totalCount || processed.length;
      state.page = page || 1;
      state.pageSize = pageSize || 20;
      state.hasNext = hasNext || false;
      state.hasPrevious = hasPrevious || false;
      state.loading = false;
      state.error = null;
    },
    addSnapshot: (
      state,
      action: PayloadAction<{ snapshot: ApiSnapshot; defaultUserName?: string }>
    ) => {
      const { snapshot, defaultUserName = 'Unknown User' } = action.payload;
      const processed = processSnapshot(snapshot, defaultUserName);
      state.snapshots.unshift(processed);
      state.totalCount += 1;
    },
    updateSnapshot: (
      state,
      action: PayloadAction<{
        snapshotId: string;
        updates: Partial<ApiSnapshot>;
        defaultUserName?: string;
      }>
    ) => {
      const { snapshotId, updates, defaultUserName = 'Unknown User' } = action.payload;
      const idx = state.snapshots.findIndex((s) => s.id === snapshotId);
      if (idx >= 0) {
        const existing = state.snapshots[idx];
        const mock: ApiSnapshot = {
          id: existing.id,
          title: updates.title || existing.title,
          description: updates.description || existing.description,
          snapshot_type: (updates.snapshot_type || existing.type) as any,
          version_number: updates.version_number || existing.version,
          is_auto_save:
            updates.is_auto_save !== undefined ? updates.is_auto_save : existing.isAutoSave,
          key_information: updates.key_information || existing.data.keyInformation,
          data_sources: updates.data_sources || {
            current_data_sources: existing.data.dataSources.internal,
            additional_data_sources: existing.data.dataSources.external,
          },
          elaborated_summary: updates.elaborated_summary,
          detailed_report: updates.detailed_report,
          process_status: updates.process_status || existing.data.processStatus,
          sub_process_status: updates.sub_process_status || '',
          step_name: updates.step_name || existing.data.step,
          custom_metadata: updates.custom_metadata || existing.data.metadata || {},
          restored_by: updates.restored_by || null,
          solution_id: updates.solution_id || existing.solutionId,
          user_id: updates.user_id || existing.userId,
          user_name: updates.user_name || existing.userName,
          created_at: updates.created_at || existing.createdAt,
          updated_at: updates.updated_at || new Date().toISOString(),
        };
        state.snapshots[idx] = processSnapshot(mock, defaultUserName);
      }
    },
    removeSnapshot: (state, action: PayloadAction<string>) => {
      state.snapshots = state.snapshots.filter((s) => s.id !== action.payload);
      state.totalCount = Math.max(0, state.totalCount - 1);
    },
    clearSnapshots: (state) => {
      state.snapshots = [];
      state.totalCount = 0;
      state.page = 1;
      state.hasNext = false;
      state.hasPrevious = false;
    },
    resetVersionHistory: () => initialState,
    refreshTimeAgo: (state) => {
      state.snapshots = state.snapshots.map((s) => ({
        ...s,
        timeAgo: formatTimeAgo(s.timestamp),
      }));
    },
  },
});

// Exports
export const {
  setLoading,
  setError,
  setCurrentSolutionId,
  updateSnapshots,
  addSnapshot,
  updateSnapshot,
  removeSnapshot,
  clearSnapshots,
  resetVersionHistory,
  refreshTimeAgo,
} = versionHistorySlice.actions;

export const selectVersionHistory = (state: { versionHistory: VersionHistoryState }) =>
  state.versionHistory;
export const selectSnapshots = (state: { versionHistory: VersionHistoryState }) =>
  state.versionHistory.snapshots;
export const selectVersionHistoryLoading = (state: { versionHistory: VersionHistoryState }) =>
  state.versionHistory.loading;
export const selectVersionHistoryError = (state: { versionHistory: VersionHistoryState }) =>
  state.versionHistory.error;
export const selectSnapshotById =
  (snapshotId: string) => (state: { versionHistory: VersionHistoryState }) =>
    state.versionHistory.snapshots.find((s) => s.id === snapshotId);

export default versionHistorySlice.reducer;
export type { ProcessedSnapshot, VersionHistoryState, ApiSnapshot };
